import { useLayoutEffect, useState } from "react";

import { Breakpoints } from "~foundations";

import { ValuesOf } from "./types";

/**
 * Custom hook that tracks the state of a media query using the Match Media API.
 *
 * NOTE: Code inspired by https://usehooks-ts.com/react-hook/use-media-query
 *
 * @example
 * ```tsx
 * // Using Breakpoints (recommended)
 * const isDesktop = useMediaQuery(Breakpoints.mediumDeviceAndUp);
 *
 * // Using custom media query strings (not recommended)
 * const isDesktop = useMediaQuery("(min-width: 900px)");
 *
 * return (
 *   <div>
 *     {isDesktop ? 'Desktop View' : 'Mobile View'}
 *   </div>
 * );
 * ```
 */
export function useMediaQuery(_query: string | ValuesOf<typeof Breakpoints>): boolean {
  /*** Constants ***/
  // Cleans up the query if it includes `@media`
  const query = _query.startsWith("@media ") ? _query.replace("@media ", "") : _query;

  /*** State ***/
  const [matches, setMatches] = useState<boolean>(() => window.matchMedia(query).matches);

  /*** Handlers ***/
  // Handles the change event of the media query.
  function handleChange() {
    setMatches(window.matchMedia(query).matches);
  }

  /*** Effects ***/
  // Handles the change event of the media query.
  useLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Use modern addEventListener/removeEventListener as recommended by MDN
    matchMedia.addEventListener("change", handleChange);

    return () => {
      matchMedia.removeEventListener("change", handleChange);
    };
  }, [query]);

  return matches;
}
